import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getHistory } from "../../services/aiInterviewService";

export default function Dashboard() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getHistory()
      .then(setSessions)
      .catch((e) => setError(e.message || "Could not load your history."))
      .finally(() => setLoading(false));
  }, []);

  const completed = sessions.filter((s) => s.status === "completed" && s.evaluation);
  const totalInterviews = completed.length;
  const scores = completed.map((s) => s.evaluation.overall || 0);
  const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  const bestScore = scores.length ? Math.max(...scores) : 0;
  const lastFive = completed.slice(0, 5).reverse();

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <p style={styles.eyebrow}>YOUR REHEARSAL LOG</p>
        <h1 style={styles.title}>Every scene you've played so far.</h1>
      </div>

      {loading && <p style={styles.muted}>Loading your history…</p>}
      {error && <p style={styles.errorText}>{error}</p>}

      {!loading && !error && sessions.length === 0 && (
        <div style={styles.emptyCard}>
          <p style={styles.emptyTitle}>No interviews yet.</p>
          <p style={styles.muted}>Your first scene is one click away.</p>
          <button style={styles.cta} onClick={() => navigate("/erp/ai-interview/setup")}>
            Start your first interview ▸
          </button>
        </div>
      )}

      {!loading && !error && sessions.length > 0 && (
        <>
          <div style={styles.statRow}>
            <div style={styles.statCard}>
              <p style={styles.statLabel}>Total interviews</p>
              <p style={styles.statValue}>{totalInterviews}</p>
            </div>
            <div style={styles.statCard}>
              <p style={styles.statLabel}>Average score</p>
              <p style={styles.statValue}>{avgScore}</p>
            </div>
            <div style={styles.statCard}>
              <p style={styles.statLabel}>Best score</p>
              <p style={styles.statValue}>{bestScore}</p>
            </div>
          </div>

          <p style={styles.sectionLabel}>RECENT SCENES</p>
          <div style={styles.list}>
            {lastFive.map((s) => (
              <div key={s.id} style={styles.row} onClick={() => navigate(`/erp/ai-interview/results`, { state: { evaluation: s.evaluation, role: s.role, type: s.type } })}>
                <div>
                  <p style={styles.rowRole}>{s.role || "Untitled role"}</p>
                  <p style={styles.rowMeta}>{s.type === "hr" ? "HR Round" : "Technical Round"} · {s.date}</p>
                </div>
                <p style={styles.rowScore}>{s.evaluation.overall}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    padding: "36px 40px",
    maxWidth: "900px",
  },
  header: {
    marginBottom: "30px",
  },
  eyebrow: {
    color: "#C99A4B",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: "11px",
    letterSpacing: "2px",
    marginBottom: "8px",
  },
  title: {
    fontFamily: "'Fraunces', serif",
    color: "#EFEAE1",
    fontSize: "26px",
    fontWeight: 600,
    margin: 0,
  },
  muted: {
    color: "#6B7488",
    fontSize: "13px",
  },
  errorText: {
    color: "#D9705A",
    fontSize: "13px",
  },
  emptyCard: {
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "16px",
    padding: "36px",
    textAlign: "center",
  },
  emptyTitle: {
    color: "#EFEAE1",
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "6px",
  },
  cta: {
    marginTop: "16px",
    background: "#C99A4B",
    color: "#10151F",
    border: "none",
    borderRadius: "10px",
    padding: "12px 20px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
  },
  statRow: {
    display: "flex",
    gap: "16px",
    marginBottom: "32px",
  },
  statCard: {
    flex: 1,
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "14px",
    padding: "18px 20px",
  },
  statLabel: {
    color: "#8A93A6",
    fontSize: "11px",
    marginBottom: "8px",
  },
  statValue: {
    fontFamily: "'Fraunces', serif",
    color: "#EFEAE1",
    fontSize: "28px",
    fontWeight: 700,
    margin: 0,
  },
  sectionLabel: {
    color: "#6B7488",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: "11px",
    letterSpacing: "1.5px",
    marginBottom: "12px",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  row: {
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "12px",
    padding: "14px 18px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  rowRole: {
    color: "#EFEAE1",
    fontSize: "14px",
    fontWeight: 600,
    margin: 0,
  },
  rowMeta: {
    color: "#6B7488",
    fontSize: "11px",
    margin: "4px 0 0 0",
  },
  rowScore: {
    color: "#C99A4B",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: "18px",
    fontWeight: 700,
  },
};
